import { useQuery, useMutation } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Game {
  scheduled_date: string;
  game_number_today: number;
  home_abbr: string;
  away_abbr: string;
  kind: string;
  series_game_number: number;
  series_length: number;
  status: string;
  sim_status: string | null;
  home_runs: number | null;
  away_runs: number | null;
  home_hits: number | null;
  away_hits: number | null;
  home_errors: number | null;
  away_errors: number | null;
  innings: number | null;
  winning_pitcher_id: number | null;
  losing_pitcher_id: number | null;
  save_pitcher_id: number | null;
  sim_game_id: number | null;
}

interface UseLeagueScheduleOptions {
  leagueId: number | string | undefined;
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
  enabled?: boolean;
}

interface LeagueScheduleResponse {
  games: Game[];
}

export type { Game };

/**
 * Hook to fetch league schedule for a date range
 * Caches results by leagueId and date range to avoid reloading when navigating
 * between already-loaded date ranges
 */
export const useLeagueSchedule = ({
  leagueId,
  startDate,
  endDate,
  enabled = true,
}: UseLeagueScheduleOptions) => {
  return useQuery<LeagueScheduleResponse>({
    queryKey: ['leagueSchedule', leagueId, startDate, endDate],
    queryFn: async () => {
      if (!leagueId) {
        throw new Error('League ID is required');
      }
      if (!startDate || !endDate) {
        throw new Error('startDate and endDate are required');
      }

      const res = await apiClient.get(`/games/schedule/${leagueId}`, {
        params: {
          startDate,
          endDate,
        },
      });
      return res.data;
    },
    enabled: enabled && !!leagueId && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 60, // Data is fresh for 1 hour (schedule doesn't change often)
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch if data is already in cache
  });
};

const SCHEDULE_MIN_DATE = '2026-03-30';
const NEXT_GAME_LOOKAHEAD_DAYS = 90;

const isGamePastEmbargo = (game: Game): boolean => {
  const hasRan = game.status?.toLowerCase() === 'final' ||
    game.status?.toLowerCase() === 'completed' ||
    game.sim_status?.toLowerCase() === 'completed';

  if (!hasRan) return false;

  const nowPst = dayjs().tz('America/Los_Angeles');
  const embargoTimePst = dayjs.tz(`${game.scheduled_date}T14:00:00`, 'America/Los_Angeles');

  return nowPst.isAfter(embargoTimePst);
};

/**
 * Hook to get the next game on the league schedule from a given date.
 * Uses useLeagueSchedule with a forward-looking range and returns the first game.
 * @param leagueId - League ID
 * @param fromDate - Optional start date (YYYY-MM-DD). Defaults to today or schedule min date.
 */
export const useNextLeagueGame = (
  leagueId: number | string | undefined,
  fromDate?: string
) => {
  const today = dayjs().tz('America/Los_Angeles').format('YYYY-MM-DD');
  const start = fromDate || (today < SCHEDULE_MIN_DATE ? SCHEDULE_MIN_DATE : today);
  const endDate = new Date(start);
  endDate.setDate(endDate.getDate() + NEXT_GAME_LOOKAHEAD_DAYS);
  const end = endDate.toISOString().slice(0, 10);

  const query = useLeagueSchedule({
    leagueId,
    startDate: start,
    endDate: end,
    enabled: !!leagueId,
  });

  const nextGame: Game | null =
    query.data?.games?.find(g => !isGamePastEmbargo(g)) || null;

  return {
    ...query,
    nextGame,
  };
};

/**
 * Hook to get the next game on the league schedule for a specific team.
 * Uses useLeagueSchedule with a forward-looking range and returns the first game involving the team.
 * @param leagueId - League ID
 * @param teamAbbr - Team abbreviation
 * @param fromDate - Optional start date (YYYY-MM-DD). Defaults to today or schedule min date.
 */
export const useNextTeamGame = (
  leagueId: number | string | undefined,
  teamAbbr: string | undefined,
  fromDate?: string
) => {
  const today = dayjs().tz('America/Los_Angeles').format('YYYY-MM-DD');
  const start = fromDate || (today < SCHEDULE_MIN_DATE ? SCHEDULE_MIN_DATE : today);
  const endDate = new Date(start);
  endDate.setDate(endDate.getDate() + NEXT_GAME_LOOKAHEAD_DAYS);
  const end = endDate.toISOString().slice(0, 10);

  const query = useLeagueSchedule({
    leagueId,
    startDate: start,
    endDate: end,
    enabled: !!leagueId && !!teamAbbr,
  });

  const nextGame: Game | null =
    query.data?.games?.find(g =>
      (g.away_abbr === teamAbbr || g.home_abbr === teamAbbr) && !isGamePastEmbargo(g)
    ) || null;

  return {
    ...query,
    nextGame,
  };
};

interface PlayGameParams {
  leagueId: number;
  homeTeamId: number;
  awayTeamId: number;
  type?: string;
  gameId?: number | null;
}

interface PlayGameResponse {
  success?: boolean;
  error?: string;
  [key: string]: any;
}

/**
 * Hook to play a game (exhibition game or existing game)
 * @returns Mutation hook for playing a game
 */
export const usePlayGame = () => {
  return useMutation<PlayGameResponse, Error, PlayGameParams>({
    mutationFn: async (params: PlayGameParams) => {
      const { leagueId, homeTeamId, awayTeamId, type = 'Exhibition Game', gameId = null } = params;

      const response = await apiClient.post('/games/play', {
        leagueId,
        homeTeamId,
        awayTeamId,
        type,
        gameId,
      });

      return response.data;
    },
  });
};
